import React, { useState } from "react";
import { Select, Checkbox } from "antd";
import { DownOutlined } from "@ant-design/icons";
import "../css/customSearchDropdown.css";
import "../css/label.css";
import "../css/customErrorForFields.css";
import "../css/customLabelDropDown.css";
import Label from "./Label";
import WarningIcon from "../../../../assets/warning.svg";
import searchIcon from "../../../../assets/Search-Icon.svg";
import LabelAndInputField from "./LabelAndInputField";

const { Option } = Select;

export default function CustomSearchDropdown({
  labelText,
  isCompulsary,
  controlId,
  controlName,
  value,
  onChange,
  options = [],
  maxTagCount = 2,
  handleSelectChange,
  errorMessage,
  isDisabled,
  placeholder,
  isRequired,
  wrapperClass,
  multiSelect = false,
  showSearch = true,
  showSelectAll = false,
  style,
  ...props
}) {
  const [searchText, setSearchText] = useState("");
  const hasError = Boolean(errorMessage);

  const filteredOptions = searchText
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(searchText.toLowerCase())
      )
    : options;

  const getSanitizedValue = () => {
    if (hasError) return undefined;

    if (multiSelect) {
      if (!Array.isArray(value)) return [];
      return value.filter(
        (v) => v !== "" && v !== null && v !== undefined
      );
    }
    return value ?? undefined;
  };

  const currentValue = getSanitizedValue() || [];

  const visibleValues = filteredOptions.map((opt) => opt.value);
  const areAllFilteredSelected =
    visibleValues.length > 0 &&
    visibleValues.every((val) => currentValue.includes(val));
  const isSomeFilteredSelected =
    visibleValues.some((val) => currentValue.includes(val)) &&
    !areAllFilteredSelected;

  const handleSelectAllChange = (e) => {
    e.stopPropagation();
    const updatedValues = areAllFilteredSelected
      ? currentValue.filter((val) => !visibleValues.includes(val))
      : Array.from(new Set([...currentValue, ...visibleValues]));

    onSelectionChange(updatedValues);
  };

  const onSelectionChange = (val) => {
    if (onChange && typeof onChange === "function") {
      onChange(val);
    }

    if (handleSelectChange && typeof handleSelectChange === "function") {
      const syntheticEvent = {
        target: {
          id: controlId,
          name: controlName,
          value: val,
        },
      };
      handleSelectChange(syntheticEvent);
    }
  };

  return (
    <div
      style={style}
      className={`custom-field-wrapper ${wrapperClass || ""}`}
    >
      {labelText && (
        <Label
          className={"custom-label"}
          labelText={labelText}
          isCompulsary={isCompulsary}
        />
      )}
      <Select
        className={`custom-dropdown ${hasError ? "dropdown-error" : ""}`}
        id={controlId}
        name={controlName}
        onChange={onSelectionChange}
        disabled={isDisabled}
        getPopupContainer={(triggerNode) => triggerNode.parentNode}
        open={isDisabled ? false : undefined}
        menuItemSelectedIcon={null}
        value={getSanitizedValue()}
        optionLabelProp="label"
        mode={multiSelect ? "multiple" : undefined}
        maxTagCount={multiSelect ? maxTagCount : undefined}
        maxTagPlaceholder={(omittedValues) => (
          <span style={{ color: "#000" }}>
            + {omittedValues.length} more
          </span>
        )}
        placeholder={placeholder}
        size="large"
        allowClear={!multiSelect}
        filterOption={false}
        shouldClearSearchOnSelect={false}
        style={style}
        showSearch={false}
        showArrow={true}
        dropdownRender={(menu) => (
          <div className="dropdown-wrapper">
            {showSearch && (
              <div
                className="dropdown-search"
                onKeyDown={(e) => e.stopPropagation()}
              >
                <LabelAndInputField
                  placeholder="Search..."
                  value={searchText}
                  handleInputChange={(e) => setSearchText(e.target.value)}
                  suffixIcon={<img src={searchIcon} alt="search" />}
                  autoFocus
                />
              </div>
            )}

            {multiSelect && showSelectAll && filteredOptions.length > 0 && (
              <div
                className="select-all-container"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleSelectAllChange}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  borderBottom: "1px solid #f0f0f0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontWeight: 600,
                }}
              >
                <span>Select All</span>
                <Checkbox
                  className="inner-checkbox"
                  checked={areAllFilteredSelected}
                  indeterminate={isSomeFilteredSelected}
                />
              </div>
            )}

            <div className="custom-options-dropdown">{menu}</div>
          </div>
        )}
        suffixIcon={
          hasError ? (
            <img
              src={WarningIcon}
              alt="error"
              className="select-error-icon"
            />
          ) : (
            <DownOutlined
              style={{
                fontSize: "12px",
                color: "#bfbfbf",
                pointerEvents: "none",
              }}
            />
          )
        }
        {...props}
      >
        {filteredOptions.map((option) => (
          <Option
            key={option.value}
            value={option.value}
            label={option.label}
          >
            {multiSelect ? (
              <div className="search-checkbox-option">
                <span>{option.label}</span>
                <Checkbox
                  className="inner-checkbox"
                  checked={currentValue.includes(option.value)}
                />
              </div>
            ) : (
              option.label
            )}
          </Option>
        ))}
      </Select>
    </div>
  );
}